/**
 * The language gate (DS-7): whether a language has everything the owner reads in
 * it, so it can be offered. English is always offered.
 *
 * A language other than English is offered only when
 *  - its resource file is complete (lib/localeCompleteness.ts);
 *  - it has every how-to guide English has, with the metadata for every one
 *    (lib/howToGuideText.ts);
 *  - it has a title and a summary for every education entry English has.
 *
 * The gate is decided at build time from the files themselves (languageGatePlugin.ts,
 * read through `virtual:language-gate`), so which languages are offered is known
 * without bundling any language's text: only English is bundled, and the others
 * are fetched when chosen (i18n.ts). The same checks run at runtime on a language
 * once it is loaded (lib/educationText.ts, lib/howToContent.ts).
 *
 * Pure: no bundler-only imports, so the Vite plugin can run it in Node.
 */
import { isCompleteTranslation } from './localeCompleteness';
import { GUIDE_FILES, HOW_TO_TEXT } from './howToGuideText';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from './language';

type Resource = Record<string, unknown>;

export interface EducationText {
  title: string;
  summary: string;
}

/** True when `text` has a title and a summary for every entry `english` has. */
export function isCompleteEducationText(
  english: Record<string, EducationText>,
  text: Record<string, EducationText> | undefined,
): boolean {
  return (
    text !== undefined &&
    Object.keys(english).every((slug) => !!text[slug]?.title?.trim() && !!text[slug]?.summary?.trim())
  );
}

/** True when `lang` has every guide file (`markdownFor(file)`) and the metadata for every guide. */
export function isCompleteHowTo(lang: Language, markdownFor: (file: string) => string | undefined): boolean {
  const text = HOW_TO_TEXT[lang];
  return text !== undefined && GUIDE_FILES.every(({ slug, file }) => !!text.guides[slug] && !!markdownFor(file)?.trim());
}

/** One language's files, as read from disk or loaded. */
export interface LanguageFiles {
  locale: Resource | undefined;
  education: Record<string, EducationText> | undefined;
  markdownFor: (file: string) => string | undefined;
}

/** The languages to offer, English first: English plus every language whose files are complete. */
export function offerableLanguages(read: (lang: Language) => LanguageFiles): Language[] {
  const english = read(DEFAULT_LANGUAGE);
  return SUPPORTED_LANGUAGES.filter((lang) => {
    if (lang === DEFAULT_LANGUAGE) return true;
    const files = read(lang);
    return (
      files.locale !== undefined &&
      isCompleteTranslation(english.locale!, files.locale, lang) &&
      isCompleteHowTo(lang, files.markdownFor) &&
      isCompleteEducationText(english.education!, files.education)
    );
  });
}
